<script lang="ts" setup>
import { watch } from "vue";
import {
  useAppKit,
  useAppKitEvents,
  useAppKitAccount,
  useDisconnect,
} from "@reown/appkit/vue";
import { useQueryClient } from "@tanstack/vue-query";

import { useNuxtApp } from "#app";

const emit = defineEmits(["saved"]);
const { $trpc } = useNuxtApp();
const queryClient = useQueryClient();

const props = defineProps<{
  campaignWeekId?: string;
  ethereumOnly?: boolean;
}>();

const modal = useAppKit();
const account = useAppKitAccount();
const events = useAppKitEvents();
const { disconnect } = useDisconnect();
const lock = ref({});

// Simple lock mechanism to ensure only one component reacts to events
const globalLock = { current: null as any };

async function openModal() {
  globalLock.current = lock.value;
  modal.open();
  trackEvent("ui_connect_wallet_widget_modal_opened", {
    trigger: "button_connect_wallet_clicked",
  });
}

watch(events, (newEvents) => {
  const eventData = newEvents.data as any;
  console.log("WalletConnector event watcher", {
    event: eventData.event,
    lockMatches: globalLock.current === lock.value,
    accountAddress: account.value.address,
  });

  // Always log all events for debugging
  if (eventData.event === "DISCONNECT_SUCCESS") {
    console.log("WalletConnector - DISCONNECT_SUCCESS detected");
    // Disconnect happened - clean up lock if it was ours
    if (globalLock.current === lock.value) {
      globalLock.current = null;
    }
    return;
  }

  if (globalLock.current !== lock.value) {
    return;
  }

  // Handle connection success - save the wallet
  if (eventData.event === "CONNECT_SUCCESS") {
    console.log("WalletConnector - CONNECT_SUCCESS, saving wallet");

    globalLock.current = null;
    resolveWalletConnected();

    trackEvent("ui_connect_wallet_widget_modal_closed", {
      trigger: "button_connect_wallet_clicked",
      wallet_address: account.value.address,
      campaign_id: props.campaignWeekId,
    });
    return;
  }

  // Handle modal close - save wallet if one is connected
  if (eventData.event === "MODAL_CLOSE") {
    const connected = eventData.properties?.connected === true;
    const hasAddress = !!account.value.address;
    console.log(
      "WalletConnector - MODAL_CLOSE, connected:",
      connected,
      "hasAddress:",
      hasAddress,
    );

    // Save wallet if connected when modal closes
    if (connected && hasAddress) {
      console.log(
        "WalletConnector - wallet connected on modal close, saving to Evangelist",
      );

      globalLock.current = null;
      resolveWalletConnected();

      trackEvent("ui_connect_wallet_widget_modal_closed", {
        trigger: "button_connect_wallet_clicked",
        wallet_address: account.value.address,
        campaign_id: props.campaignWeekId,
      });
    } else {
      console.log("WalletConnector - no wallet connected, NOT saving");
      globalLock.current = null;
    }
  }
});

async function resolveWalletConnected() {
  console.log("resolving wallet address: " + JSON.stringify(account.value));
  if (!account.value.caipAddress) {
    errorMessage("Wallet connection failed");
    return;
  }

  const [namespace] = account.value.caipAddress.split(":");

  // Check if this is a campaign that requires Ethereum wallets only
  if (props.campaignWeekId && namespace !== "eip155") {
    await disconnect();
    trackEvent("wallet_connection_failed", {
      trigger: "connect_wallet_widget_wallet_connected",
      reason: "unsupported_chain",
      wallet_address: account.value.address,
      namespace,
    });
    errorMessage("Please select an Ethereum wallet for this campaign.");
    return;
  }

  // Check if this component is configured for Ethereum only (e.g., from campaign sidebar)
  if (props.ethereumOnly && namespace !== "eip155") {
    await disconnect();
    trackEvent("wallet_connection_failed", {
      trigger: "connect_wallet_widget_wallet_connected",
      reason: "ethereum_only_required",
      wallet_address: account.value.address,
      namespace,
    });
    errorMessage("Please select an Ethereum wallet.");
    return;
  }

  try {
    if (props.campaignWeekId) {
      const response = await $trpc.connectWalletToCampaignWeek.mutate({
        campaignWeekId: props.campaignWeekId,
        caipAddress: account.value.caipAddress,
      });

      trackEvent("user_wallet_connected", {
        trigger: "connect_wallet_widget_wallet_connected",
        wallet_address: account.value.address,
        campaign_week_id: props.campaignWeekId,
      });

      if (response.reused) {
        successMessage(
          `Wallet ${account.value.address} already connected for this campaign.`,
        );
      } else {
        successMessage(
          "Wallet connected. Verify ownership to unlock this reward.",
        );
      }
    } else {
      const response = await $trpc.addWallet.mutate({
        caipAddress: account.value.caipAddress,
      });
      if (response.reused) {
        successMessage(`Wallet ${account.value.address} already connected.`);
      } else {
        trackEvent("user_wallet_connected", {
          trigger: "connect_wallet_widget_wallet_connected",
          wallet_address: account.value.address,
        });
        successMessage("Wallet connected.");
      }
    }

    // Immediately invalidate the wallet list query to refresh the UI
    if (props.campaignWeekId) {
      await queryClient.invalidateQueries({
        queryKey: ["listCampaignWeekWallets", props.campaignWeekId],
      });
    }

    emit("saved");
  } catch (error) {
    console.error(error);
    trackEvent("wallet_connection_failed");
    errorMessage("Adding wallet failed.");
  }
}
</script>

<template>
  <button @click="openModal">
    <slot />
  </button>
</template>
